import React, { useState, useEffect, lazy, Suspense, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import brandLogo from "@/assets/regard-de-nature-logo.png";
import heroImage from "@/assets/font_site_vitrine.webp";
import naturalApproach from "@/assets/comprendre-besoins.png";
import structuredAccompaniment from "@/assets/structured-accompaniment.webp";
import holisticBalance from "@/assets/approche-douce.png";
import bookingBackground from "@/assets/booking-background.webp";
import { BookingModal } from "@/components/BookingModal";
import { LegalNotice } from "@/components/LegalNotice";
import { PrivacyPolicy } from "@/components/PrivacyPolicy";
import { TermsOfService } from "@/components/TermsOfService";
import { EthicsCharter } from "@/components/EthicsCharter";
import { CGV } from "@/components/CGV";
import { CartIcon } from "@/components/cart/CartIcon";
import { CartDrawer } from "@/components/cart/CartDrawer";
import { CookieConsent } from "@/components/CookieConsent";
import { NewsletterForm } from "@/components/NewsletterForm";
import { NewsletterPopup } from "@/components/NewsletterPopup";
import { BRAND } from "@/config/brand";
const IntroAnimation = lazy(() => import("@/components/IntroAnimation"));
import { SeoLinksHomeSection } from "@/components/seo/SeoLinksHomeSection";
import { TestimonialsSection } from "@/components/TestimonialsSection";
import { Reveal } from "@/components/ui/reveal";
import { MobileBookingBar } from "@/components/MobileBookingBar";


// Lazy load non-critical sections below the fold
const EventsSection = lazy(() => import("@/components/EventsSection").then((m) => ({ default: m.EventsSection })));
const ProductsSection = lazy(() =>
  import("@/components/ProductsSection").then((m) => ({ default: m.ProductsSection })),
);
const SeasonalWellnessSection = lazy(() =>
  import("@/components/SeasonalWellnessSection").then((m) => ({ default: m.SeasonalWellnessSection })),
);
const GiftCardSection = lazy(() =>
  import("@/components/GiftCardSection").then((m) => ({ default: m.GiftCardSection })),
);

import {
  Leaf,
  Heart,
  Moon,
  Smile,
  Sparkles,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Clock,
  Star,
  ChevronDown,
  Lock,
  MessageCircle,
  Video,
  Home,
  Menu,
  X,
  Eye,
  Award,
  Facebook,
  Instagram,
  Linkedin,
  Info,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

interface Accompaniment {
  id: string;
  title: string;
  short_description: string;
  full_description: string | null;
  price: number;
  duration_minutes: number;
  image_url: string | null;
  is_active: boolean;
  booking_enabled: boolean;
  cabinet_booking_enabled: boolean;
  visio_booking_enabled: boolean;
  icon_name: string;
}

// Composant pour afficher le nom de marque stylisé
const BrandName = ({ size = "default" }: { size?: "small" | "default" | "large" | "hero" }) => {
  const sizeClasses = {
    small: { line1: "text-sm", line2: "text-sm", baseline: "text-[8px]" },
    default: { line1: "text-lg md:text-xl", line2: "text-lg md:text-xl", baseline: "text-[9px] md:text-[10px]" },
    large: { line1: "text-xl md:text-2xl", line2: "text-xl md:text-2xl", baseline: "text-[10px] md:text-xs" },
    hero: {
      line1: "text-3xl sm:text-5xl md:text-6xl",
      line2: "text-3xl sm:text-5xl md:text-6xl",
      baseline: "text-xs sm:text-sm md:text-base",
    },
  };

  const classes = sizeClasses[size];

  return (
    <div className="text-center">
      <div className={`font-cinzel font-semibold tracking-wide`}>
        <span className={`${classes.line1} text-secondary`}>{BRAND.name.line1}</span>
        <br />
        <span className={`${classes.line2} text-primary`}>{BRAND.name.line2}</span>
      </div>
      <p className={`${classes.baseline} font-montserrat font-semibold uppercase tracking-wider mt-1`}>
        <span className="text-primary">{BRAND.baseline.parts.text1}</span>
        <span className="text-secondary"> {BRAND.baseline.parts.separator} </span>
        <span className="text-primary">{BRAND.baseline.parts.text2}</span>
      </p>
    </div>
  );
};

// Returns a maps URL adapted to the user's device (Apple Maps on iOS, Google Maps elsewhere)
const getMapsUrl = (address: string): string => {
  const encoded = encodeURIComponent(address);
  if (typeof navigator !== "undefined") {
    const ua = navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes("Mac") && "ontouchend" in document);
    if (isIOS) return `https://maps.apple.com/?q=${encoded}`;
  }
  return `https://www.google.com/maps/search/?api=1&query=${encoded}`;
};

const Landing = () => {
  const location = useLocation();
  const [showIntro, setShowIntro] = useState(() => {
    // Afficher l'intro uniquement à la première visite de la session
    const hasSeenIntro = sessionStorage.getItem("hasSeenIntro");
    if (hasSeenIntro) return false;
    // Accessibilité : pas d'animation si l'utilisateur préfère les animations réduites
    if (typeof window !== "undefined") {
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reducedMotion) {
        sessionStorage.setItem("hasSeenIntro", "true");
        return false;
      }
    }
    return true;
  });
  const [openFaq, setOpenFaq] = useState<string>("");
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<any>(null);
  const [isOnlineBooking, setIsOnlineBooking] = useState(false);
  const [isHomeVisitBooking, setIsHomeVisitBooking] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedBenefit, setSelectedBenefit] = useState<number | null>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [accompaniments, setAccompaniments] = useState<Accompaniment[]>([]);
  const [openAccompanimentId, setOpenAccompanimentId] = useState<string | null>(null);
  const [practitionerLoading, setPractitionerLoading] = useState<boolean>(true);
  const [practitionerPhoto, setPractitionerPhoto] = useState<string | null>(null);
  const [practitionerPhoto2, setPractitionerPhoto2] = useState<string | null>(null);
  const [practitionerPhoto3, setPractitionerPhoto3] = useState<string | null>(null);
  const [practitionerName, setPractitionerName] = useState<string>("Laura");
  const [hasCertification, setHasCertification] = useState<boolean>(false);
  const [publicSettings, setPublicSettings] = useState<{
    contact_email_public: string;
    facebook_url: string | null;
    instagram_url: string | null;
    linkedin_url: string | null;
    seo_section_enabled: boolean;
  }>({
    contact_email_public: 'contact@regard-de-nature.fr',
    facebook_url: null,
    instagram_url: null,
    linkedin_url: null,
    seo_section_enabled: true,
  });

  const handleIntroComplete = useCallback(() => {
    sessionStorage.setItem("hasSeenIntro", "true");
    setShowIntro(false);
  }, []);

  // Recharger les données à chaque navigation vers la landing page
  useEffect(() => {
    loadAccompaniments();
    loadPractitionerInfo();
    loadPublicSettings();
  }, [location.pathname]);

  // Gérer le défilement vers une ancre lors d'une navigation avec hash
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      // Laisser le temps au DOM de s'hydrater (sections lazy-load)
      const timer = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [location.hash, location.pathname]);

  // Ouvrir une fiche accompagnement via ?open=<slug>
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const slug = params.get("open");
    if (!slug || accompaniments.length === 0) return;
    const slugify = (s: string) =>
      s
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    const match = accompaniments.find((a) => slugify(a.title) === slug);
    if (match) setOpenAccompanimentId(match.id);
  }, [location.search, accompaniments]);


  const loadAccompaniments = async () => {
    try {
      const { data, error } = await supabase
        .from("accompaniments")
        .select("*")
        .eq("is_active", true)
        .order("display_order");

      if (error) throw error;
      setAccompaniments(data || []);
    } catch (error) {
      console.error("Erreur chargement accompagnements:", error);
    }
  };

  const loadPractitionerInfo = async () => {
    setPractitionerLoading(true);
    // Évite tout flash d'une image résiduelle pendant le chargement
    setPractitionerPhoto(null);
    setPractitionerPhoto2(null);
    setPractitionerPhoto3(null);

    try {
      const { data, error } = await supabase.rpc('get_practitioner_public_display');

      if (data && data.length > 0 && !error) {
        const practitioner = data[0];
        if (practitioner.photo_url) setPractitionerPhoto(practitioner.photo_url);
        if (practitioner.photo_url_2) setPractitionerPhoto2(practitioner.photo_url_2);
        if (practitioner.photo_url_3) setPractitionerPhoto3(practitioner.photo_url_3);
        if (practitioner.practitioner_name) setPractitionerName(practitioner.practitioner_name);
        if (practitioner.certification_url) setHasCertification(true);
      }
    } catch (error) {
      console.error("Erreur chargement info praticien:", error);
    } finally {
      setPractitionerLoading(false);
    }
  };

  const loadPublicSettings = async () => {
    try {
      const { data, error } = await supabase.rpc('get_public_app_settings');
      if (data && data.length > 0 && !error) {
        const settings = data[0];
        setPublicSettings({
          contact_email_public: settings.contact_email_public || 'contact@regard-de-nature.fr',
          facebook_url: settings.facebook_url,
          instagram_url: settings.instagram_url,
          linkedin_url: settings.linkedin_url,
          seo_section_enabled: settings.seo_section_enabled !== false,
        });
      }
    } catch (error) {
      console.error("Erreur chargement settings publics:", error);
    }
  };

  const scrollToAccompagnements = () => {
    const element = document.getElementById("accompagnements");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleBookingClick = (service: any, isOnline: boolean) => {
    setSelectedService(service);
    setIsOnlineBooking(isOnline);
    setIsHomeVisitBooking(false);
    setBookingModalOpen(true);
  };

  const handleAccompanimentBookingClick = (
    accompaniment: Accompaniment,
    isOnline: boolean,
    isHomeVisit = false
  ) => {
    const serviceData = {
      title: accompaniment.title,
      duration: accompaniment.duration_minutes,
      tarif: `${accompaniment.price}€`,
      description: accompaniment.short_description,
      image: accompaniment.image_url || "",
    };
    setSelectedService(serviceData);
    setIsOnlineBooking(isOnline);
    setIsHomeVisitBooking(isHomeVisit);
    setBookingModalOpen(true);
  };

  // Accompagnements éligibles à la consultation à domicile
  const isHomeVisitEligible = (title: string) => {
    const t = title
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
    return (
      (t.includes("bilan") && (t.includes("enfant") || t.includes("adolescent"))) ||
      (t.includes("bilan") && t.includes("adulte")) ||
      (t.includes("parcours") && t.includes("famille"))
    );
  };

  const benefits = [
    {
      icon: <Eye className="w-6 h-6" />,
      title: "Comprendre vos besoins",
      description: "Identifier les causes de vos déséquilibres pour agir de manière ciblée.",
      image: naturalApproach,
      detailedDescription:
        "Nous prenons le temps d'analyser votre fonctionnement, vos habitudes afin de mieux comprendre l'origine de vos difficultés.\n\nCette étape permet de proposer des solutions adaptées, plutôt que des conseils génériques.",
    },
    {
      icon: <Leaf className="w-6 h-6" />,
      title: "Des solutions concrètes, adaptées à votre quotidien.",
      description: "Des conseils simples, réalistes et faciles à mettre en place.",
      image: structuredAccompaniment,
      detailedDescription:
        "Chaque accompagnement suit une structure claire, avec des étapes définies et des recommandations personnalisées.\n\nLes conseils sont progressifs pour s'intégrer facilement dans votre routine, sans pression ni culpabilité.",
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Une approche douce, respectueuse de votre rythme",
      description: "Prendre en compte votre équilibre dans sa globalité, sans approche standardisée.",
      image: holisticBalance,
      detailedDescription:
        "L'accompagnement prend en compte l'ensemble de votre situation : alimentation, rythme de vie, environnement et état émotionnel.\n\nIl s'inscrit en complément d'un suivi médical, dans une démarche responsable et cohérente.",
    },
  ];

  const faqCategories = [
    {
      title: "Avant de commencer",
      questions: [
        {
          question: "Est-ce que cet accompagnement est fait pour moi ?",
          answer:
            "Si vous ressentez le besoin de retrouver de l'énergie, d'apaiser certains déséquilibres ou de mieux comprendre votre fonctionnement, l'accompagnement peut vous être bénéfique.\n\nIl s'adresse aussi bien aux adultes qu'aux enfants, avec des adaptations en fonction de chaque situation.",
        },
        {
          question: "Puis-je prendre rendez-vous même sans problématique précise ?",
          answer:
            "Oui.\n\nL'accompagnement peut aussi s'inscrire dans une démarche de prévention, pour préserver votre équilibre et mieux comprendre votre fonctionnement.",
        },
        {
          question:
            "Et si je ne sais pas par où commencer ou si je ne suis pas sûr(e) que cet accompagnement me corresponde ?",
          answer:
            "C'est une question tout à fait normale.\n\nQuand on hésite, c'est souvent que l'on ressent le besoin de changer quelque chose, sans forcément savoir par où commencer.\n\nLe bilan permet justement de faire un premier point, de comprendre votre situation et de vous proposer des solutions adaptées, sans engagement dans la durée.\n\nVous pouvez également me contacter pour échanger simplement si vous avez besoin d'être rassuré(e) avant de prendre rendez-vous.",
        },
      ],
    },
    {
      title: "Pendant l'accompagnement",
      questions: [
        {
          question: "Combien de séances sont nécessaires ?",
          answer:
            "Le nombre de séances dépend de votre situation et de vos objectifs.\n\nLe bilan permet de faire un premier point, et des séances de suivi peuvent être proposées si nécessaire.",
        },
        {
          question: "Est-ce que les conseils sont adaptés à mon quotidien ?",
          answer:
            "Oui.\n\nLes recommandations sont personnalisées et pensées pour s'intégrer dans votre quotidien, de manière progressive et réaliste.",
        },
        {
          question: "Quand vais-je ressentir les premiers résultats ?",
          answer:
            "Les premiers changements peuvent apparaître dès les premières semaines.\n\nLes résultats dépendent ensuite de votre situation, de votre rythme et de la mise en place des recommandations.",
        },
      ],
    },
    {
      title: "Informations pratiques",
      questions: [
        {
          question: "Les séances sont-elles remboursées ?",
          answer:
            "Certaines mutuelles prennent en charge les consultations de naturopathie.\n\nJe vous invite à vous rapprocher de votre organisme pour connaître les modalités de remboursement.\n\nUne facture vous sera remise à l'issue de la séance pour vos démarches.",
        },
        {
          question: "Comment se déroule le règlement des séances ?",
          answer: "Le règlement peut se faire en espèces et en carte bancaire.",
        },
      ],
    },
  ];

  return (
    <>
      {/* Animation d'introduction */}
      {showIntro && (
        <Suspense fallback={null}>
          <IntroAnimation onAnimationComplete={handleIntroComplete} />
        </Suspense>
      )}
      
      <div className={`min-h-screen bg-background ${showIntro ? 'opacity-0' : 'opacity-100 transition-opacity duration-500'}`}>
      {/* Header / Navigation */}
      <header className="sticky top-0 z-50 w-full border-b border-border bg-card/95 backdrop-blur-sm">
        <div className="container mx-auto px-3 sm:px-4 py-2 sm:py-3 md:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <img src={brandLogo} alt={BRAND.name.full} width={343} height={313} className="w-9 sm:w-10 md:w-12 h-auto object-contain" loading="eager" decoding="async" />
            <div className="text-left">
              <div className="font-cinzel font-bold leading-tight">
                <span className="text-secondary text-sm sm:text-base md:text-lg">{BRAND.name.line1}</span>
                <span className="text-primary text-sm sm:text-base md:text-lg"> {BRAND.name.line2}</span>
              </div>
              <p className="text-[8px] sm:text-[9px] md:text-[10px] font-montserrat uppercase tracking-wide">
                <span className="text-primary">{BRAND.baseline.parts.text1}</span>
                <span className="text-secondary"> {BRAND.baseline.parts.separator} </span>
                <span className="text-primary">{BRAND.baseline.parts.text2}</span>
              </p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            <a
              href="#accueil"
              className="text-sm font-medium text-foreground hover:text-primary transition-colors font-montserrat"
            >
              Accueil
            </a>
            <a
              href="#accompagnements"
              className="text-sm font-medium text-foreground hover:text-primary transition-colors font-montserrat"
            >
              Accompagnements
            </a>
            <Link
              to="/mon-approche"
              className="text-sm font-medium text-foreground hover:text-primary transition-colors font-montserrat"
            >
              Mon approche
            </Link>
            <a
              href="#temoignages"
              className="text-sm font-medium text-foreground hover:text-primary transition-colors font-montserrat"
            >
              Avis
            </a>
            <a
              href="#faq"
              className="text-sm font-medium text-foreground hover:text-primary transition-colors font-montserrat"
            >
              FAQ
            </a>
            <Link
              to="/contact"
              className="text-sm font-medium text-foreground hover:text-primary transition-colors font-montserrat"
            >
              Contact
            </Link>
          </nav>

          {/* Desktop Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <Button
              className="bg-gradient-primary text-primary-foreground hover:opacity-90 font-montserrat"
              onClick={scrollToAccompagnements}
            >
              <Calendar className="w-4 h-4 mr-2" />
              Réserver un rendez-vous
            </Button>
            <Link to="/dashboard">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-muted-foreground hover:text-primary font-montserrat"
              >
                <Lock className="w-3 h-3 mr-1" />
                Espace Pro
              </Button>
            </Link>
          </div>

          {/* Mobile Menu */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" aria-label="Ouvrir le menu de navigation">
                <Menu className="w-5 h-5" aria-hidden="true" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[280px] sm:w-[350px] bg-card"
              aria-label="Menu de navigation mobile"
            >
              <div className="flex flex-col gap-6 mt-8">
                <div className="flex items-center gap-3 pb-4 border-b border-border">
                  <img src={brandLogo} alt={BRAND.name.full} width={343} height={313} className="w-10 h-auto object-contain" loading="eager" decoding="async" />
                  <div>
                    <div className="font-cinzel font-bold">
                      <span className="text-secondary text-lg">{BRAND.name.line1}</span>
                      <span className="text-primary text-lg"> {BRAND.name.line2}</span>
                    </div>
                    <p className="text-[9px] font-montserrat uppercase tracking-wide">
                      <span className="text-primary">{BRAND.baseline.parts.text1}</span>
                      <span className="text-secondary"> {BRAND.baseline.parts.separator} </span>
                      <span className="text-primary">{BRAND.baseline.parts.text2}</span>
                    </p>
                  </div>
                </div>

                <nav className="flex flex-col gap-4">
                  <a
                    href="#accueil"
                    className="text-base font-medium text-foreground hover:text-primary transition-colors py-2 font-montserrat"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Accueil
                  </a>
                  <a
                    href="#accompagnements"
                    className="text-base font-medium text-foreground hover:text-primary transition-colors py-2 font-montserrat"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Accompagnements
                  </a>
                  <Link
                    to="/mon-approche"
                    className="text-base font-medium text-foreground hover:text-primary transition-colors py-2 font-montserrat"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Mon approche
                  </Link>
                  <a
                    href="#temoignages"
                    className="text-base font-medium text-foreground hover:text-primary transition-colors py-2 font-montserrat"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Avis
                  </a>
                  <a
                    href="#faq"
                    className="text-base font-medium text-foreground hover:text-primary transition-colors py-2 font-montserrat"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    FAQ
                  </a>
                  <Link
                    to="/contact"
                    className="text-base font-medium text-foreground hover:text-primary transition-colors py-2 font-montserrat"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Contact
                  </Link>
                </nav>

                <div className="flex flex-col gap-3 pt-4 border-t border-border">
                  <Button
                    className="w-full h-12 bg-gradient-primary text-primary-foreground hover:opacity-90 font-montserrat"
                    onClick={() => {
                      scrollToAccompagnements();
                      setMobileMenuOpen(false);
                    }}
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Réserver un rendez-vous
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full h-12 font-montserrat"
                    onClick={() => {
                      setContactDialogOpen(true);
                      setMobileMenuOpen(false);
                    }}
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Échanger avec moi
                  </Button>
                  <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-xs text-muted-foreground hover:text-primary font-montserrat"
                    >
                      <Lock className="w-3 h-3 mr-1" />
                      Espace Pro
                    </Button>
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Hero Section */}
      <section id="accueil" className="relative overflow-hidden pt-6 sm:pt-8 md:pt-16 pb-12 sm:pb-16 md:pb-32">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Naturopathie - Accompagnement naturel vers l'équilibre"
            width={1386}
            height={924}
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-secondary/10" />
        </div>

        <div className="container mx-auto px-3 sm:px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-4 sm:space-y-6 md:space-y-8">
            <div className="flex flex-col items-center justify-center space-y-2 sm:space-y-4">
              <img src={brandLogo} alt={BRAND.name.full} width={343} height={313} className="w-24 sm:w-36 md:w-48 h-auto object-contain" loading="eager" decoding="async" fetchPriority="high" />
              <BrandName size="hero" />
            </div>
            <h1 className="text-base sm:text-xl md:text-2xl lg:text-3xl font-cinzel font-bold text-primary leading-tight px-2 uppercase">
              <span className="block">Accompagner les parents et leurs enfants</span>
              <span className="block">vers un équilibre durable</span>
            </h1>
            <div className="text-sm sm:text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto px-2 font-montserrat space-y-3">
              <p>Fatigue, stress, enfants sensibles ou agités…</p>
              <p>Des solutions naturelles adaptées à votre réalité.</p>
              <p>J'accompagne également les adultes souhaitant retrouver équilibre, énergie et bien-être au quotidien.</p>
              <p className="italic">Maman de deux enfants aux besoins particuliers, j'accompagne avec une approche à la fois professionnelle, humaine et ancrée dans le réel.</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center pt-4 sm:pt-6 px-2">
              <Button
                size="lg"
                className="w-full sm:w-auto bg-gradient-primary text-primary-foreground hover:opacity-90 h-12 sm:h-14 px-6 sm:px-8 text-sm sm:text-base font-montserrat"
                onClick={scrollToAccompagnements}
              >
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Réserver un rendez-vous
              </Button>

              <Link to="/mon-approche" className="w-full sm:w-auto">
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full sm:w-auto h-12 sm:h-14 px-6 sm:px-8 text-sm sm:text-base font-montserrat border-primary/30 hover:bg-primary/5"
                >
                  <Eye className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Découvrir mon approche
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="apropos" className="py-12 sm:py-16 md:py-24 bg-muted/30" aria-labelledby="benefits-heading">
        <div className="container mx-auto px-3 sm:px-4">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 id="benefits-heading" className="text-2xl sm:text-3xl md:text-4xl font-cinzel font-bold text-primary mb-3 sm:mb-4 uppercase">
              Un accompagnement adapté à votre réalité
            </h2>
            <p className="text-base sm:text-lg text-foreground/80 max-w-5xl mx-auto font-montserrat px-2">
              <span className="block">Chaque situation est différente, et mérite une approche adaptée à votre quotidien.</span>
              <span className="block mt-3">Mon accompagnement prend en compte les défis de votre réalité et celle de votre enfant.</span>
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="relative h-full flex flex-col">
                {/* Desktop: HoverCard */}
                <div className="hidden lg:flex lg:flex-col lg:h-full">
                  <HoverCard openDelay={200} closeDelay={100}>
                    <HoverCardTrigger asChild>
                      <Card
                        className="group cursor-pointer border-0 shadow-card hover:shadow-glow transition-all duration-300 transform hover:-translate-y-1 overflow-hidden h-full"
                      >
                        <div className="h-32 sm:h-40 md:h-48 overflow-hidden">
                          <img
                            src={benefit.image}
                            alt={benefit.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" />
                        </div>
                        <CardHeader className="text-center pb-2">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto bg-gradient-primary rounded-full flex items-center justify-center text-primary-foreground mb-2 sm:mb-3 group-hover:scale-110 transition-transform">
                            {benefit.icon}
                          </div>
                          <CardTitle className="text-base sm:text-lg md:text-xl font-cinzel text-primary">
                            {benefit.title}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="text-center pb-4 sm:pb-6 flex-1 flex items-end justify-center">
                          <p className="text-xs sm:text-sm text-muted-foreground font-montserrat">{benefit.description}</p>
                        </CardContent>
                      </Card>
                    </HoverCardTrigger>
                    <HoverCardContent className="w-96 p-0 overflow-hidden" sideOffset={8}>
                      <div className="animate-fade-in">
                        <img src={benefit.image} alt={benefit.title} className="w-full h-32 object-cover" loading="lazy" decoding="async" />
                        <div className="p-4">
                          <h4 className="text-lg font-semibold text-primary mb-3 font-cinzel">{benefit.title}</h4>
                          <p className="text-sm text-muted-foreground leading-relaxed font-montserrat whitespace-pre-line">
                            {benefit.detailedDescription}
                          </p>
                        </div>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                </div>

                {/* Mobile/Tablet: Click to expand */}
                <div className="lg:hidden">
                  <Card
                    className={`group cursor-pointer border-0 shadow-card transition-all duration-300 overflow-hidden ${
                      selectedBenefit === index ? "shadow-glow ring-2 ring-primary/20" : ""
                    }`}
                    onClick={() => setSelectedBenefit(selectedBenefit === index ? null : index)}
                  >
                    <div className="h-32 sm:h-40 overflow-hidden">
                      <img
                        src={benefit.image}
                        alt={benefit.title}
                        className="w-full h-full object-cover" loading="lazy" decoding="async" />
                    </div>
                    <CardHeader className="text-center pb-2">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto bg-gradient-primary rounded-full flex items-center justify-center text-primary-foreground mb-2 sm:mb-3">
                        {benefit.icon}
                      </div>
                      <CardTitle className="text-base sm:text-lg font-cinzel text-primary">
                        {benefit.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-center pb-4">
                      <p className="text-xs sm:text-sm text-muted-foreground font-montserrat">{benefit.description}</p>
                      <div className="mt-2 text-xs text-primary/60 font-montserrat flex items-center justify-center gap-1">
                        <span>{selectedBenefit === index ? "Fermer" : "En savoir plus"}</span>
                        <ChevronDown className={`w-3 h-3 transition-transform duration-300 ${selectedBenefit === index ? "rotate-180" : ""}`} />
                      </div>
                    </CardContent>
                    
                    {/* Expanded content for mobile */}
                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        selectedBenefit === index ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                      }`}
                    >
                      <div className="px-4 pb-4 pt-2 border-t border-border/50">
                        <p className="text-sm text-muted-foreground leading-relaxed font-montserrat whitespace-pre-line">
                          {benefit.detailedDescription}
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Accompanies You Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            {/* Title centered */}
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-cinzel font-bold text-primary uppercase">
                Une approche née de mon parcours
              </h2>
            </div>

            {/* Mobile : layout vertical classique */}
            <div className="flex flex-col items-center gap-8 md:hidden">
              <div className="flex-shrink-0">
                {practitionerLoading ? (
                  <div className="w-72 h-72 sm:w-80 sm:h-80 rounded-full overflow-hidden shadow-elegant border-4 border-background bg-muted animate-pulse" />
                ) : practitionerPhoto ? (
                  <div className="group w-72 h-72 sm:w-80 sm:h-80 rounded-full overflow-hidden shadow-elegant border-4 border-background transition-all duration-500 ease-out hover:scale-105 hover:shadow-2xl cursor-pointer">
                    <img
                      src={practitionerPhoto}
                      alt={practitionerName}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" decoding="async" />
                  </div>
                ) : (
                  <div className="w-72 h-72 sm:w-80 sm:h-80 rounded-full shadow-elegant border-4 border-primary/10 bg-muted flex items-center justify-center">
                    <Leaf className="w-10 h-10 text-primary/70" aria-hidden="true" />
                    <span className="sr-only">Photo du praticien</span>
                  </div>
                )}
              </div>
              <div className="text-center">
                <div className="space-y-4 text-foreground/80 font-montserrat leading-relaxed">
                  <p className="text-base sm:text-lg">
                    Je suis <span className="font-semibold text-primary">Laura</span>, naturopathe.
                  </p>
                  <p className="text-sm sm:text-base">
                    C'est mon rôle de maman de deux enfants aux besoins spécifiques qui a profondément orienté mon parcours.
                  </p>
                  <p className="text-sm sm:text-base">
                    Confrontée à des défis concrets du quotidien, j'ai ressenti le besoin de trouver des solutions naturelles, efficaces et adaptées à la réalité des familles.
                  </p>
                  <p className="text-sm sm:text-base">
                    Cette démarche m'a conduite à me former à la naturopathie au sein de l'<span className="font-semibold">Alternative Medicine Center</span>, où j'ai suivi un cursus complet de <span className="font-semibold">1045 heures</span>, me permettant d'acquérir une approche globale de la santé, notamment en alimentation, micronutrition et phytothérapie.
                  </p>
                  <p className="text-sm sm:text-base">
                    Aujourd'hui, j'accompagne adultes et familles qui se sentent fatigués, dépassés ou en perte d'équilibre, parfois liés à une surcharge mentale, avec une approche simple et personnalisée, pensée pour s'adapter à leur quotidien.
                  </p>
                  <p className="text-sm sm:text-base font-medium text-primary">
                    Mon objectif : vous aider à retrouver énergie, équilibre et sérénité, avec des actions concrètes, réalistes et durables.
                  </p>
                </div>
                <div className="mt-6 flex flex-wrap gap-3 justify-center">
                  <Link to="/mon-approche" className="w-full sm:w-auto">
                    <Button variant="outline" className="font-montserrat w-full sm:w-auto">
                      <Leaf className="w-4 h-4 mr-2" />
                      Découvrir mon approche
                    </Button>
                  </Link>
                  {hasCertification && (
                    <Link to="/certification" className="w-full sm:w-auto">
                      <Button variant="outline" className="font-montserrat w-full sm:w-auto">
                        <Award className="w-4 h-4 mr-2" />
                        Voir ma certification
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </div>

            {/* Desktop : texte en arc épousant la courbure de la photo (shape-outside) */}
            <div className="hidden md:block">
              <div className="text-foreground/80 font-montserrat leading-relaxed">
                {/* Forme circulaire flottante : la photo + son shape-outside */}
                <div
                  className="float-left mr-8"
                  style={{
                    shapeOutside: "circle(50% at 50% 50%)",
                    WebkitShapeOutside: "circle(50% at 50% 50%)" as any,
                    shapeMargin: "1.5rem",
                  } as React.CSSProperties}
                >
                  {practitionerLoading ? (
                    <div className="w-[26rem] h-[26rem] rounded-full overflow-hidden shadow-elegant border-4 border-background bg-muted animate-pulse" />
                  ) : practitionerPhoto ? (
                    <div className="group w-[26rem] h-[26rem] rounded-full overflow-hidden shadow-elegant border-4 border-background transition-all duration-500 ease-out hover:scale-105 hover:shadow-2xl cursor-pointer">
                      <img
                        src={practitionerPhoto}
                        alt={practitionerName}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" decoding="async" />
                    </div>
                  ) : (
                    <div className="w-[26rem] h-[26rem] rounded-full shadow-elegant border-4 border-primary/10 bg-muted flex items-center justify-center">
                      <Leaf className="w-10 h-10 text-primary/70" aria-hidden="true" />
                      <span className="sr-only">Photo du praticien</span>
                    </div>
                  )}
                </div>

                <p className="text-lg mb-4">
                  Je suis <span className="font-semibold text-primary">Laura</span>, naturopathe.
                </p>
                <p className="text-base mb-4">
                  C'est mon rôle de maman de deux enfants aux besoins spécifiques qui a profondément orienté mon parcours.
                </p>
                <p className="text-base mb-4">
                  Confrontée à des défis concrets du quotidien, j'ai ressenti le besoin de trouver des solutions naturelles, efficaces et adaptées à la réalité des familles.
                </p>
                <p className="text-base mb-4">
                  Cette démarche m'a conduite à me former à la naturopathie au sein de l'<span className="font-semibold">Alternative Medicine Center</span>, où j'ai suivi un cursus complet de <span className="font-semibold">1045 heures</span>, me permettant d'acquérir une approche globale de la santé, notamment en alimentation, micronutrition et phytothérapie.
                </p>
                <p className="text-base mb-4">
                  Aujourd'hui, j'accompagne adultes et familles qui se sentent fatigués, dépassés ou en perte d'équilibre, parfois liés à une surcharge mentale, avec une approche simple et personnalisée, pensée pour s'adapter à leur quotidien.
                </p>
                <p className="text-base font-medium text-primary mb-6">
                  Mon objectif : vous aider à retrouver énergie, équilibre et sérénité, avec des actions concrètes, réalistes et durables.
                </p>

                <div className="clear-both" />
                <div className="flex flex-wrap gap-3 justify-center mt-6">
                  <Link to="/mon-approche">
                    <Button variant="outline" className="font-montserrat">
                      <Leaf className="w-4 h-4 mr-2" />
                      Découvrir mon approche
                    </Button>
                  </Link>
                  {hasCertification && (
                    <Link to="/certification">
                      <Button variant="outline" className="font-montserrat">
                        <Award className="w-4 h-4 mr-2" />
                        Voir ma certification
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Where to Consult Section — déplacée avant les Domaines pour : lieu → problématique */}
      <section
        id="ou-me-consulter"
        className="py-12 sm:py-16 md:py-20 bg-muted/30"
        aria-labelledby="ou-me-consulter-heading"
      >
        <div className="container mx-auto px-3 sm:px-4">
          <div className="text-center mb-8 sm:mb-10">
            <h2
              id="ou-me-consulter-heading"
              className="text-2xl sm:text-3xl md:text-4xl font-cinzel font-bold text-primary mb-3"
            >
              Où me consulter
            </h2>
            <div className="w-20 h-px bg-secondary mx-auto mb-4" />
            <p className="text-base sm:text-lg text-foreground/80 max-w-2xl mx-auto font-montserrat">
              Un lieu d'accueil dans le Lauragais, des consultations à domicile sur Toulouse Est / Sud-Est et le Lauragais, et la visio partout en France.
            </p>
          </div>


          <Reveal className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* Montesquieu-Lauragais */}
            <Card className="border-border/50 hover:shadow-elegant transition-shadow duration-300 flex flex-col">
              <CardHeader>
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <Home className="w-6 h-6 text-primary" />
                </div>
                <CardTitle className="font-cinzel text-lg sm:text-xl text-primary">
                  Consultations au cabinet à Montesquieu-Lauragais
                </CardTitle>
                <CardDescription className="font-montserrat text-foreground/70">
                  Espace dédié au sein de mon lieu de vie
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col">
                <p className="text-sm sm:text-base text-foreground/80 font-montserrat leading-relaxed mb-4">
                  31 rue de l'ancien petit fossé, 31450 Montesquieu-Lauragais.
                  <br />
                  <span className="text-foreground/70 italic">Stationnement sur place.</span>
                </p>
                <div className="mt-auto flex flex-col sm:flex-row gap-2">
                  <Button
                    asChild
                    variant="outline"
                    size="sm"
                    className="self-start font-montserrat border-primary/40 text-primary hover:bg-primary/10"
                  >
                    <a
                      href={getMapsUrl("31 rue de l'ancien petit fossé, 31450 Montesquieu-Lauragais")}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Voir l'adresse de Montesquieu-Lauragais sur une carte"
                    >
                      <MapPin className="w-4 h-4 mr-2" />
                      Voir sur la carte
                    </a>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    size="sm"
                    className="self-start font-montserrat border-primary/40 text-primary hover:bg-primary/10"
                  >
                    <Link
                      to="/naturopathe-montesquieu-lauragais"
                      aria-label="En savoir plus sur le cabinet de Montesquieu-Lauragais"
                    >
                      <Info className="w-4 h-4 mr-2" />
                      En savoir plus
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Consultations à domicile */}
            <Card className="border-border/50 hover:shadow-elegant transition-shadow duration-300 flex flex-col">
              <CardHeader>
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <Home className="w-6 h-6 text-primary" />
                </div>
                <CardTitle className="font-cinzel text-lg sm:text-xl text-primary">
                  Consultations à domicile
                </CardTitle>
                <CardDescription className="font-montserrat text-foreground/70">
                  Votre accompagnement naturopathique, directement chez vous
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col">
                <p className="text-sm sm:text-base text-foreground/80 font-montserrat leading-relaxed mb-3">
                  Pas besoin de vous déplacer : je viens à votre domicile pour vous offrir un temps d'échange
                  confortable, dans votre environnement.
                </p>
                <p className="text-sm sm:text-base text-foreground/80 font-montserrat leading-relaxed mb-3">
                  Disponible sur Toulouse et son agglomération Est / Sud-Est, ainsi que dans le Lauragais jusqu'à
                  Villefranche-de-Lauragais.
                </p>
                <ul className="text-sm sm:text-base text-foreground/80 font-montserrat space-y-1 mb-3">
                  <li>✔ Confort</li>
                  <li>✔ Gain de temps</li>
                  <li>✔ Accompagnement personnalisé</li>
                </ul>
                <p className="text-sm text-foreground/70 italic font-montserrat mb-4">
                  Pour les bilans naturopathiques et le Parcours Famille Courageuse.
                </p>
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className="mt-auto self-start font-montserrat border-primary/40 text-primary hover:bg-primary/10"
                >
                  <Link
                    to="/naturopathe-domicile-toulouse-lauragais"
                    aria-label="En savoir plus sur les consultations de naturopathie à domicile"
                  >
                    <Info className="w-4 h-4 mr-2" />
                    En savoir plus
                  </Link>
                </Button>
              </CardContent>
            </Card>


            {/* Consultations en visio */}
            <Card className="border-border/50 hover:shadow-elegant transition-shadow duration-300 flex flex-col">
              <CardHeader>
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <Video className="w-6 h-6 text-primary" />
                </div>
                <CardTitle className="font-cinzel text-lg sm:text-xl text-primary">
                  Consultations en visio
                </CardTitle>
                <CardDescription className="font-montserrat text-foreground/70">
                  Quand se déplacer devient compliqué
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col">
                <p className="text-sm sm:text-base text-foreground/80 font-montserrat leading-relaxed mb-3">
                  Parents débordés, emploi du temps chargé, distance, enfants à gérer…
                </p>
                <p className="text-sm sm:text-base text-foreground/80 font-montserrat leading-relaxed mb-3">
                  Profitez d'un accompagnement naturopathique depuis chez vous, partout en France.
                </p>
                <ul className="text-sm sm:text-base text-foreground/80 font-montserrat space-y-1 mb-4">
                  <li>✔ Confort</li>
                  <li>✔ Simplicité</li>
                  <li>✔ Flexibilité</li>
                </ul>
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className="mt-auto self-start font-montserrat border-primary/40 text-primary hover:bg-primary/10"
                >
                  <Link
                    to="/consultations-visio-naturopathe-france"
                    aria-label="En savoir plus sur les consultations en visio"
                  >
                    <Video className="w-4 h-4 mr-2" />
                    En savoir plus
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </Reveal>
        </div>
      </section>

      {/* SEO Links Section - Domaines d'accompagnement */}
      {publicSettings.seo_section_enabled && <SeoLinksHomeSection />}

      {/* Accompaniments Section */}
      <section id="accompagnements" className="py-12 sm:py-16 md:py-24">
        <div className="container mx-auto px-3 sm:px-4">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-cinzel font-bold text-primary mb-3 sm:mb-4">
              Mes accompagnements
            </h2>
            <div className="text-base sm:text-lg text-foreground/80 max-w-2xl mx-auto font-montserrat space-y-3">
              <p className="font-semibold text-primary">Par où commencer&nbsp;?</p>
              <p>
                Le bilan naturopathique est la première étape pour comprendre votre situation et mettre en place un accompagnement adapté, que ce soit pour vous ou pour votre enfant.
              </p>
            </div>
          </div>

          {accompaniments.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground font-montserrat">Aucun accompagnement disponible pour le moment.</p>
            </div>
          ) : (
            <Reveal className="flex flex-wrap justify-center gap-4 sm:gap-6 md:gap-8">
              {accompaniments.map((accompaniment) => (
                <Card
                  key={accompaniment.id}
                  className="overflow-hidden shadow-card hover:shadow-glow transition-all duration-300 flex flex-col w-full sm:w-[calc(50%-0.75rem)] md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.34rem)]"
                >
                  {accompaniment.image_url && (
                    <div className="h-32 sm:h-40 md:h-48 overflow-hidden">
                      <img
                        src={accompaniment.image_url}
                        alt={accompaniment.title}
                        className="w-full h-full object-cover" loading="lazy" decoding="async" />
                    </div>
                  )}
                  <CardHeader className="pb-2 flex-1">
                    <CardTitle className="text-lg sm:text-xl font-cinzel text-primary">{accompaniment.title}</CardTitle>
                    <CardDescription className="font-montserrat">{accompaniment.short_description}</CardDescription>
                    {accompaniment.full_description && (
                      <Dialog
                        open={openAccompanimentId === accompaniment.id}
                        onOpenChange={(o) => setOpenAccompanimentId(o ? accompaniment.id : null)}
                      >
                        <DialogTrigger asChild>
                          <button className="flex items-center gap-1 text-sm text-secondary hover:text-primary font-montserrat transition-colors mt-2 underline underline-offset-4">
                            <Info className="w-4 h-4" />
                            En savoir plus
                          </button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                          {accompaniment.image_url && (
                            <div className="w-full h-48 overflow-hidden rounded-lg mb-4">
                              <img
                                src={accompaniment.image_url}
                                alt={accompaniment.title}
                                className="w-full h-full object-cover" loading="lazy" decoding="async" />
                            </div>
                          )}
                          <DialogHeader>
                            <DialogTitle className="font-cinzel text-primary text-xl">{accompaniment.title}</DialogTitle>
                            <DialogDescription className="font-montserrat text-muted-foreground">
                              {accompaniment.duration_minutes} min • {accompaniment.price}€
                            </DialogDescription>
                          </DialogHeader>
                          <div className="mt-4 space-y-4">
                            <div className="prose prose-sm max-w-none font-montserrat text-foreground whitespace-pre-line">
                              {accompaniment.full_description}
                            </div>
                            {accompaniment.booking_enabled && (
                              <div className="flex flex-col gap-2 pt-4 border-t">
                                {accompaniment.cabinet_booking_enabled && (
                                  <Button
                                    className="w-full bg-gradient-primary font-montserrat"
                                    onClick={() => handleAccompanimentBookingClick(accompaniment, false)}
                                  >
                                    <MapPin className="w-4 h-4 mr-2" />
                                    Réserver au cabinet
                                  </Button>
                                )}
                                {accompaniment.visio_booking_enabled && (
                                  <Button
                                    variant="outline"
                                    className="w-full font-montserrat"
                                    onClick={() => handleAccompanimentBookingClick(accompaniment, true)}
                                  >
                                    <Video className="w-4 h-4 mr-2" />
                                    Réserver en visio
                                  </Button>
                                )}
                                {isHomeVisitEligible(accompaniment.title) && (
                                  <Button
                                    variant="outline"
                                    className="w-full font-montserrat"
                                    onClick={() => handleAccompanimentBookingClick(accompaniment, false, true)}
                                  >
                                    <Home className="w-4 h-4 mr-2" />
                                    Réserver à domicile
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                  </CardHeader>
                  <CardContent className="mt-auto">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground font-montserrat">
                        <Clock className="w-4 h-4" />
                        <span>{accompaniment.duration_minutes} min</span>
                      </div>
                      <Badge variant="secondary" className="font-montserrat">
                        {accompaniment.price}€
                      </Badge>
                    </div>

                    {accompaniment.booking_enabled && (
                      <div className="flex flex-col gap-2">
                        {accompaniment.cabinet_booking_enabled && (
                          <Button
                            className="w-full bg-gradient-primary font-montserrat"
                            onClick={() => handleAccompanimentBookingClick(accompaniment, false)}
                          >
                            <MapPin className="w-4 h-4 mr-2" />
                            Réserver au cabinet
                          </Button>
                        )}
                        {accompaniment.visio_booking_enabled && (
                          <Button
                            variant="outline"
                            className="w-full font-montserrat"
                            onClick={() => handleAccompanimentBookingClick(accompaniment, true)}
                          >
                            <Video className="w-4 h-4 mr-2" />
                            Réserver en visio
                          </Button>
                        )}
                        {isHomeVisitEligible(accompaniment.title) && (
                          <Button
                            variant="outline"
                            className="w-full font-montserrat"
                            onClick={() => handleAccompanimentBookingClick(accompaniment, false, true)}
                          >
                            <Home className="w-4 h-4 mr-2" />
                            Réserver à domicile
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Reveal>
          )}
        </div>
      </section>

      {/* Events Section - Lazy loaded */}
      <Suspense
        fallback={
          <div className="py-16 text-center">
            <div className="animate-pulse h-32 bg-muted/30 rounded-lg max-w-4xl mx-auto" />
          </div>
        }
      >
        <EventsSection />
      </Suspense>

      {/* Products Section - Lazy loaded */}
      <Suspense
        fallback={
          <div className="py-16 text-center">
            <div className="animate-pulse h-32 bg-muted/30 rounded-lg max-w-4xl mx-auto" />
          </div>
        }
      >
        <ProductsSection />
      </Suspense>

      {/* Seasonal Wellness Section - Lazy loaded */}
      <Suspense
        fallback={
          <div className="py-16 text-center">
            <div className="animate-pulse h-32 bg-muted/30 rounded-lg max-w-4xl mx-auto" />
          </div>
        }
      >
        <SeasonalWellnessSection />
      </Suspense>

      {/* Gift Card Section - Lazy loaded */}
      <Suspense
        fallback={
          <div className="py-16 text-center">
            <div className="animate-pulse h-32 bg-muted/30 rounded-lg max-w-4xl mx-auto" />
          </div>
        }
      >
        <GiftCardSection />
      </Suspense>

      {/* Testimonials */}
      <TestimonialsSection />

      {/* FAQ Section */}
      <section id="faq" className="py-12 sm:py-16 md:py-24 bg-muted/30" aria-labelledby="faq-heading">
        <div className="container mx-auto px-3 sm:px-4">
          <div className="text-center mb-8 sm:mb-12">
            <h2
              id="faq-heading"
              className="text-2xl sm:text-3xl md:text-4xl font-cinzel font-bold text-primary mb-3 sm:mb-4"
            >
              Questions fréquentes
            </h2>
            <p className="text-base sm:text-lg text-foreground/80 max-w-2xl mx-auto font-montserrat">
              Des réponses simples pour vous aider à aborder votre accompagnement en toute sérénité.
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-8 sm:space-y-10">
            {faqCategories.map((category, catIndex) => (
              <div key={catIndex}>
                <h3 className="text-lg sm:text-xl font-cinzel font-semibold text-secondary mb-3 sm:mb-4 uppercase tracking-wide">
                  {category.title}
                </h3>
                <Accordion type="single" collapsible>
                  {category.questions.map((faq, qIndex) => (
                    <AccordionItem
                      key={qIndex}
                      value={`faq-${catIndex}-${qIndex}`}
                      className="border-b border-border"
                    >
                      <AccordionTrigger className="text-left text-sm sm:text-base font-medium text-primary hover:text-secondary font-montserrat py-4">
                        <h4 className="text-left font-medium">{faq.question}</h4>
                      </AccordionTrigger>
                      <AccordionContent className="text-sm sm:text-base text-foreground/80 font-montserrat leading-relaxed whitespace-pre-line">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>

          {/* Call to Action */}
          <div className="max-w-3xl mx-auto mt-10 sm:mt-12 text-center">
            <p className="text-base sm:text-lg text-foreground/80 font-montserrat mb-6">
              Si vous avez encore des questions ou souhaitez échanger, vous pouvez me contacter ou prendre rendez-vous.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-gradient-primary font-montserrat h-12 px-8"
                onClick={scrollToAccompagnements}
              >
                <Calendar className="w-5 h-5 mr-2" />
                Prendre rendez-vous
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="font-montserrat h-12 px-8 border-primary text-primary hover:bg-primary/10"
                onClick={() => setContactDialogOpen(true)}
              >
                <Mail className="w-5 h-5 mr-2" />
                Échanger avec moi
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section
        id="contact"
        className="py-12 sm:py-16 md:py-24 relative overflow-hidden"
        style={{
          backgroundImage: `url(${bookingBackground})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        aria-labelledby="contact-heading"
      >
        <div className="absolute inset-0 bg-primary/80" />
        <div className="container mx-auto px-3 sm:px-4 relative z-10">
          <div className="max-w-2xl mx-auto text-center text-primary-foreground">
            <h2 id="contact-heading" className="text-2xl sm:text-3xl md:text-4xl font-cinzel font-bold mb-4 sm:mb-6">
              Prêt(e) à prendre soin de vous ?
            </h2>
            <p className="text-base sm:text-lg mb-6 sm:mb-8 opacity-90 font-montserrat">
              Réservez votre première consultation et faites un premier pas vers un mieux-être durable.
            </p>
            <Button
              size="lg"
              className="bg-secondary text-secondary-foreground hover:bg-secondary-hover h-12 sm:h-14 px-6 sm:px-10 text-sm sm:text-lg font-montserrat"
              onClick={scrollToAccompagnements}
            >
              <Calendar className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
              Réserver un rendez-vous
            </Button>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section id="newsletter" className="py-12 sm:py-16 md:py-20 bg-card" aria-labelledby="newsletter-heading">
        <div className="container mx-auto px-3 sm:px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-secondary/10 mb-6">
              <Mail className="w-8 h-8 text-secondary" />
            </div>
            <NewsletterForm variant="default" showTitle={true} showDescription={true} />
          </div>
        </div>
      </section>
      <footer className="bg-card border-t border-border py-8 sm:py-12 md:py-16">
        <div className="container mx-auto px-3 sm:px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 md:gap-12 mb-6 sm:mb-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img src={brandLogo} alt={BRAND.name.full} width={343} height={313} className="w-8 sm:w-10 h-auto object-contain" loading="eager" decoding="async" />
                <div>
                  <div className="font-cinzel font-bold">
                    <span className="text-secondary text-sm sm:text-base">{BRAND.name.line1}</span>
                    <span className="text-primary text-sm sm:text-base"> {BRAND.name.line2}</span>
                  </div>
                  <p className="text-[8px] sm:text-[9px] font-montserrat uppercase tracking-wide">
                    <span className="text-primary">{BRAND.baseline.parts.text1}</span>
                    <span className="text-secondary"> {BRAND.baseline.parts.separator} </span>
                    <span className="text-primary">{BRAND.baseline.parts.text2}</span>
                  </p>
                </div>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground font-montserrat">
                Accompagnement naturel vers l'équilibre et la vitalité
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-primary mb-2 sm:mb-4 text-sm sm:text-base font-cinzel">Navigation</h4>
              <ul className="space-y-1 sm:space-y-2 text-xs sm:text-sm text-muted-foreground font-montserrat">
                <li>
                  <a href="#accueil" className="hover:text-primary transition-colors">
                    Accueil
                  </a>
                </li>
                <li>
                  <a href="#accompagnements" className="hover:text-primary transition-colors">
                    Accompagnements
                  </a>
                </li>
                <li>
                  <Link to="/tarifs" className="hover:text-primary transition-colors">
                    Tarifs &amp; accompagnements
                  </Link>
                </li>
                <li>
                  <Link to="/mon-approche" className="hover:text-primary transition-colors">
                    Mon approche
                  </Link>
                </li>
                <li>
                  <a href="#faq" className="hover:text-primary transition-colors">
                    FAQ
                  </a>
                </li>
                <li>
                  <Link to="/contact" className="hover:text-primary transition-colors">
                    Contact
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-primary mb-2 sm:mb-4 text-sm sm:text-base font-cinzel">Légal</h4>
              <ul className="space-y-1 sm:space-y-2 text-xs sm:text-sm text-muted-foreground font-montserrat">
                <li>
                  <LegalNotice />
                </li>
                <li>
                  <PrivacyPolicy />
                </li>
                <li>
                  <TermsOfService />
                </li>
                <li>
                  <EthicsCharter />
                </li>
                <li>
                  <CGV />
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-primary mb-2 sm:mb-4 text-sm sm:text-base font-cinzel">Contact</h4>
              <div className="space-y-3">
                <a 
                  href={`mailto:${publicSettings.contact_email_public}`}
                  className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground hover:text-primary transition-colors font-montserrat"
                >
                  <Mail className="w-4 h-4" />
                  {publicSettings.contact_email_public}
                </a>
                
                {/* Réseaux sociaux */}
                {(publicSettings.facebook_url || publicSettings.instagram_url || publicSettings.linkedin_url) && (
                  <div className="flex items-center gap-3 pt-2">
                    {publicSettings.facebook_url && (
                      <a 
                        href={publicSettings.facebook_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-blue-600 transition-colors"
                        aria-label="Facebook"
                      >
                        <Facebook className="w-5 h-5" />
                      </a>
                    )}
                    {publicSettings.instagram_url && (
                      <a 
                        href={publicSettings.instagram_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-pink-600 transition-colors"
                        aria-label="Instagram"
                      >
                        <Instagram className="w-5 h-5" />
                      </a>
                    )}
                    {publicSettings.linkedin_url && (
                      <a 
                        href={publicSettings.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-blue-700 transition-colors"
                        aria-label="LinkedIn"
                      >
                        <Linkedin className="w-5 h-5" />
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="border-t border-border pt-6 sm:pt-8 text-center text-xs sm:text-sm text-muted-foreground font-montserrat">
            <p>{BRAND.legal.copyright(new Date().getFullYear())}</p>
            <p className="mt-2 text-[10px] sm:text-xs px-2">{BRAND.legal.disclaimer}</p>
          </div>
        </div>
      </footer>

      {/* Booking Modal */}
      <BookingModal
        open={bookingModalOpen}
        onOpenChange={setBookingModalOpen}
        service={selectedService}
        isOnline={isOnlineBooking}
        isHomeVisit={isHomeVisitBooking}
      />

      {/* Cart Icon and Drawer */}
      <CartIcon onClick={() => setCartOpen(true)} />
      <CartDrawer open={cartOpen} onOpenChange={setCartOpen} />

      {/* Contact Dialog (Échanger avec moi) */}
      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-cinzel text-primary">Échanger avec moi</DialogTitle>
            <DialogDescription className="font-montserrat">
              Choisissez votre mode de contact préféré
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <a
              href="sms:+33637031866"
              className="flex items-center gap-4 p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
            >
              <div className="w-12 h-12 bg-primary-light rounded-lg flex items-center justify-center text-primary flex-shrink-0">
                <MessageCircle className="w-6 h-6" />
              </div>
              <div>
                <p className="font-semibold text-primary font-montserrat">Par sms</p>
                <p className="text-sm text-muted-foreground font-montserrat">06 37 03 18 66</p>
              </div>
            </a>
            <a
              href="mailto:contact@regard-de-nature.fr"
              className="flex items-center gap-4 p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
            >
              <div className="w-12 h-12 bg-secondary-light rounded-lg flex items-center justify-center text-secondary flex-shrink-0">
                <Mail className="w-6 h-6" />
              </div>
              <div>
                <p className="font-semibold text-primary font-montserrat">Par email</p>
                <p className="text-sm text-muted-foreground font-montserrat">contact@regard-de-nature.fr</p>
              </div>
            </a>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cookie Consent Banner */}
      <CookieConsent />

      {/* Newsletter Popup */}
      <NewsletterPopup />
      {/* Barre de réservation persistante mobile */}
      <MobileBookingBar />
      </div>
    </>
  );
};

export default Landing;
